/* eslint-disable @typescript-eslint/no-explicit-any */
import Peer from '@bowbee/peer-lite';
import {
  PlaycastWebrtcPreset,
  PlaycastWebrtcEncoder,
  RTCRtpCodecCapability,
  PlaycastWebrtcPresetGroup,
  PlaycastChannels,
  PlaycastPeerMediaMetadata,
  H264EncodingConfig,
} from './typesMedia';
import { debug } from './util';
import { StateStore } from '@playcastdotio/StateStore';
import { defaultQualityPresetStore, h264Config } from './defaultConfigs';
import { PlaycastPeerClientMetadata, PlaycastPeerHostMetadata } from './types';

const allAvailableEncodings: PlaycastWebrtcEncoder[] = ['vp8', 'vp9', 'h264', 'av1'];

let availableVideoEncodingTypes: PlaycastWebrtcEncoder[] = [];
const sdpFmtpLines: { [key in PlaycastWebrtcEncoder]: string[] } = {
  vp8: [],
  vp9: [],
  h264: [],
  av1: [],
};

export const getAvailableVideoEncodingTypes = (): PlaycastWebrtcEncoder[] => {
  if (availableVideoEncodingTypes.length !== 0) {
    return availableVideoEncodingTypes;
  }
  const capabilities = RTCRtpSender.getCapabilities('video');
  if (!capabilities || !capabilities.codecs) {
    throw new Error('Failed to get video codec capabilities');
  }
  const supportedCodecs: PlaycastWebrtcEncoder[] = [];
  capabilities.codecs.forEach((codec) => {
    const encodingType = codec.mimeType.split('/')[1].toLowerCase() as PlaycastWebrtcEncoder;
    if (allAvailableEncodings.includes(encodingType)) {
      supportedCodecs.push(encodingType);
    }
  });
  availableVideoEncodingTypes = supportedCodecs;
  return supportedCodecs;
};

let receiverCapabilities: RTCRtpCodecCapability[] = [];
export const getReceiverCapabilities = (): RTCRtpCodecCapability[] => {
  if (receiverCapabilities.length !== 0) {
    return receiverCapabilities;
  }
  const capabilities = RTCRtpReceiver.getCapabilities('video');
  if (!capabilities || !capabilities.codecs) {
    throw new Error('Failed to get video codec capabilities');
  }
  receiverCapabilities = capabilities.codecs;
  receiverCapabilities.forEach((codec) => {
    if (codec.mimeType === 'video/VP8') {
      sdpFmtpLines.vp8.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/VP9') {
      sdpFmtpLines.vp9.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/H264') {
      sdpFmtpLines.h264.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/AV1') {
      sdpFmtpLines.av1.push(codec.sdpFmtpLine || '');
    }
  });
  return receiverCapabilities;
};

const findBestH264SdpLinesForIos = (): RTCRtpCodecCapability[] => {
  const h264Lines = sdpFmtpLines.h264;
  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to check profile and level
  const hasProfileAndLevel = (line: string, profile: string, minLevel: string) => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    if (!match) return false;
    const profileId = match[1].toLowerCase();
    return profileId.startsWith(profile) && profileId.substring(4) >= minLevel;
  };

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  // Try Baseline with at least level 3.1 first (most compatible)
  const baseline = h264Lines.find((line) => hasProfileAndLevel(line, '4200', '1f'));
  if (baseline) capabilities.push(createCapability(baseline));

  // Then try Constrained Baseline with at least level 3.1
  const constrainedBaseline = h264Lines.find((line) => hasProfileAndLevel(line, '42e0', '1f'));
  if (constrainedBaseline) capabilities.push(createCapability(constrainedBaseline));

  // Then try Main Profile with at least level 3.1
  const mainProfile = h264Lines.find((line) => hasProfileAndLevel(line, '4d00', '1f'));
  if (mainProfile) capabilities.push(createCapability(mainProfile));

  // Add any remaining lines as fallbacks
  h264Lines.forEach((line) => {
    if (!capabilities.some((cap) => cap.sdpFmtpLine === line)) {
      capabilities.push(createCapability(line));
    }
  });

  return capabilities;
};

const findBestH264SdpLines = (isApple?: boolean, isFirefox?: boolean): RTCRtpCodecCapability[] => {
  console.log('[Presets] Finding best H264 SDP lines', isApple, isFirefox);
  const h264Lines = sdpFmtpLines.h264;
  if (!h264Lines.length) return [];

  // Helper to check hardware encoding parameters
  const isHardwareCapable = (line: string): boolean => {
    return line.includes('packetization-mode=1') && line.includes('level-asymmetry-allowed=1');
  };

  // Get profile ID from line
  const getProfileId = (line: string): string | null => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    return match ? match[1].toLowerCase() : null;
  };

  // Known profiles that might work for both hardware encoding and iOS/Firefox
  const potentialProfiles = [
    '4d001f', // Main Profile Level 3.1 (4D001F)
    '42c01f', // Constrained Baseline Level 3.1 (alternative to 42e01f)
    '42c028', // Constrained Baseline Level 4.0
    '42401f', // Baseline Level 3.1 (alternative)
    '424028', // Baseline Level 4.0
    '4d401f', // Main Level 3.1
    '4d4028', // Main Level 4.0
  ];

  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  if (isFirefox) {
    // Firefox works best with software encoding for H264
    const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));
    if (softwareLines.length > 0) {
      console.warn('[H264] Using software encoding for Firefox:', softwareLines);
      return softwareLines.map(createCapability);
    }
  }

  // Try to find hardware-capable lines with known-good profiles
  for (const profile of potentialProfiles) {
    const compatibleLine = h264Lines.find((line) => {
      const currentProfile = getProfileId(line);
      return isHardwareCapable(line) && currentProfile === profile;
    });

    if (compatibleLine) {
      console.debug(`[H264] Found compatible profile with hardware encoding: ${profile}`);
      capabilities.push(createCapability(compatibleLine));
    }
  }

  // If no hardware-capable lines found, try software encoding
  if (capabilities.length === 0) {
    const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));
    if (softwareLines.length > 0) {
      console.warn('[H264] Falling back to software encoding');
      return softwareLines.map(createCapability);
    }
  }

  // Log available profiles for debugging
  console.debug('[H264] Available profiles:');
  h264Lines.forEach((line) => {
    const profile = getProfileId(line);
    const hasHardware = isHardwareCapable(line);
    console.debug(`Profile: ${profile}, Hardware Capable: ${hasHardware}`);
  });

  // If still no capabilities, add all lines as fallbacks
  if (capabilities.length === 0) {
    console.warn('[H264] No optimal profiles found, using all available lines');
    return h264Lines.map(createCapability);
  }

  return capabilities;
};

const findBestH264SdpLinesForFirefox = (): RTCRtpCodecCapability[] => {
  const h264Lines = sdpFmtpLines.h264;
  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to check hardware encoding parameters
  const isHardwareCapable = (line: string): boolean => {
    return line.includes('packetization-mode=1') && line.includes('level-asymmetry-allowed=1');
  };

  // Helper to check profile and level
  const hasProfileAndLevel = (line: string, profile: string, minLevel: string) => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    if (!match) return false;
    const profileId = match[1].toLowerCase();
    return profileId.startsWith(profile) && profileId.substring(4) >= minLevel;
  };

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  // Firefox works best with software encoding for H264
  // First try to find software-encoded lines with known-good profiles
  const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));

  // Try Baseline profile first (most compatible)
  const baseline = softwareLines.find((line) => hasProfileAndLevel(line, '4200', '1f'));
  if (baseline) capabilities.push(createCapability(baseline));

  // Then try Constrained Baseline
  const constrainedBaseline = softwareLines.find((line) => hasProfileAndLevel(line, '42e0', '1f'));
  if (constrainedBaseline) capabilities.push(createCapability(constrainedBaseline));

  // Then try Main Profile
  const mainProfile = softwareLines.find((line) => hasProfileAndLevel(line, '4d00', '1f'));
  if (mainProfile) capabilities.push(createCapability(mainProfile));

  // Add any remaining software-encoded lines as fallbacks
  softwareLines.forEach((line) => {
    if (!capabilities.some((cap) => cap.sdpFmtpLine === line)) {
      capabilities.push(createCapability(line));
    }
  });

  // If we found no software-encoded lines, log a warning
  if (capabilities.length === 0) {
    console.warn('[H264] No software-encoded lines found for Firefox, falling back to hardware encoding');
    // Fall back to hardware encoding as last resort
    const hardwareLines = h264Lines.filter((line) => isHardwareCapable(line));
    if (hardwareLines.length > 0) {
      return hardwareLines.map(createCapability);
    }
  }

  return capabilities;
};

export const convertPlaycastEncodingTypeToSdpLines = (
  encodingTypes: PlaycastWebrtcEncoder | PlaycastWebrtcEncoder[],
  peerMetadata: PlaycastPeerClientMetadata,
  lineOverride: RTCRtpCodecCapability | undefined,
): RTCRtpCodecCapability[] | undefined => {
  if (!Array.isArray(encodingTypes)) {
    encodingTypes = [encodingTypes];
  }

  console.log('[Presets] Line override:', peerMetadata.type, lineOverride);
  console.log('[Presets] Encoding types:', peerMetadata.type, encodingTypes);

  // For Firefox, return undefined to avoid renegotiation
  if (peerMetadata?.isFirefoxBrowser) {
    console.log('[Presets] Firefox detected, returning undefined to avoid renegotiation');
    return undefined;
  }

  const availableSenders = getAvailableVideoEncodingTypes();
  const availableReceivers = getReceiverCapabilities();

  console.log('[Presets] Available senders:', availableSenders);
  console.log('[Presets] Available receivers:', availableReceivers);
  console.log('[Presets] Peer metadata:', peerMetadata);

  const types = encodingTypes
    .map((encodingType): RTCRtpCodecCapability[] | undefined => {
      switch (encodingType) {
        case 'vp8':
          return lineOverride?.mimeType === 'video/VP8'
            ? [lineOverride]
            : sdpFmtpLines.vp8.length > 0
              ? sdpFmtpLines.vp8
                  .filter((line) => line) // Filter out empty lines
                  .map((line) => ({
                    mimeType: 'video/VP8',
                    clockRate: 90000,
                    sdpFmtpLine: line,
                  }))
              : undefined;
        case 'vp9':
          return lineOverride?.mimeType === 'video/VP9'
            ? [lineOverride]
            : sdpFmtpLines.vp9.length > 0
              ? sdpFmtpLines.vp9
                  .filter((line) => line) // Filter out empty lines
                  .map((line) => ({
                    mimeType: 'video/VP9',
                    clockRate: 90000,
                    sdpFmtpLine: line,
                  }))
              : undefined;
        case 'h264': {
          if (lineOverride?.mimeType === 'video/H264') {
            return [lineOverride];
          }

          if (sdpFmtpLines.h264.length === 0) {
            return undefined;
          }

          // For iOS, use the iOS-specific function
          if (peerMetadata?.isAppleDevice) {
            return findBestH264SdpLinesForIos();
          }

          // For other devices, use the general function
          return findBestH264SdpLines(peerMetadata?.isAppleDevice);
        }
        case 'av1':
          return lineOverride?.mimeType === 'video/AV1'
            ? [lineOverride]
            : sdpFmtpLines.av1.length > 0
              ? sdpFmtpLines.av1
                  .filter((line) => line) // Filter out empty lines
                  .map((line) => ({
                    mimeType: 'video/AV1',
                    clockRate: 90000,
                    sdpFmtpLine: line,
                  }))
              : undefined;
        default:
          throw new Error(`Unsupported encoding type: ${encodingType}`);
      }
    })
    .filter((type): type is RTCRtpCodecCapability[] => type !== undefined);

  // Flatten the array of arrays
  const flattenedTypes = types.flat();

  // Filter based on available senders and receivers, ensuring exact matches
  let result = flattenedTypes.filter((type) => {
    const codecType = type.mimeType.split('/')[1].toLowerCase() as PlaycastWebrtcEncoder;
    return (
      availableSenders.includes(codecType) &&
      availableReceivers.some(
        (recv) => recv.mimeType === type.mimeType && recv.clockRate === type.clockRate && recv.sdpFmtpLine === type.sdpFmtpLine,
      )
    );
  });

  // For H264, ensure we have at least one valid SDP line
  result = result.filter((type) => {
    if (type.mimeType === 'video/H264') {
      return type.sdpFmtpLine !== undefined;
    }
    return true;
  });

  console.debug('[Codecs]', result);

  // Fallback to VP8 if no valid codecs are found
  if (result.length === 0) {
    const vp8Capability = availableReceivers.find((recv) => recv.mimeType === 'video/VP8');
    if (vp8Capability) {
      console.log('[Presets] Falling back to VP8');
      result.push(vp8Capability);
    } else if (availableReceivers.length > 0) {
      console.log('[Presets] No VP8 available, using first available codec');
      result.push(availableReceivers[0]);
    }
  }

  return result;
};

export const transformSdp = (sdp: RTCSessionDescriptionInit, preset: PlaycastWebrtcPresetGroup['default']) => {
  if (!sdp.sdp) return sdp;

  // Split into sections
  const sections = sdp.sdp.split('\r\nm=');

  // Process each section
  const processedSections = sections.map((section, index) => {
    // Add back the 'm=' prefix that was removed by split
    const fullSection = index === 0 ? section : `m=${section}`;

    // Skip non-video sections
    if (!fullSection.startsWith('m=video')) {
      return fullSection;
    }

    // Split into lines
    const lines = fullSection.split('\r\n');

    // Find the rtpmap line for H264
    let h264PayloadType: string | null = null;
    for (const line of lines) {
      if (line.startsWith('a=rtpmap:') && line.includes('H264/90000')) {
        h264PayloadType = line.split(':')[1].split(' ')[0];
        break;
      }
    }

    // If no H264 found, return section unchanged
    if (!h264PayloadType) {
      return fullSection;
    }

    // Process fmtp lines
    const processedLines = lines.map((line) => {
      if (line.startsWith(`a=fmtp:${h264PayloadType}`)) {
        // Get existing parameters
        const existingParams = line.split(' ')[1] || '';

        // Remove any existing parameters we're about to add
        let cleanParams = existingParams
          .replace(/sps-pps-idr-in-keyframe=1;?/, '')
          .replace(/x-google-max-bitrate=\d+;?/, '')
          .replace(/x-google-min-bitrate=\d+;?/, '')
          .replace(/x-google-start-bitrate=\d+;?/, '')
          .replace(/;$/, ''); // Remove trailing semicolon if present

        // Add new parameters if configured
        const newParams: string[] = [];
        if (h264Config?.enableSpsPpsIdrInKeyframe) {
          newParams.push('sps-pps-idr-in-keyframe=1');
        }
        if (h264Config?.enableMaxBitrate && h264Config.maxBitrate) {
          newParams.push(`x-google-max-bitrate=${h264Config.maxBitrate}`);
        }
        if (h264Config?.enableMinBitrate && h264Config.minBitrate) {
          newParams.push(`x-google-min-bitrate=${h264Config.minBitrate}`);
        }
        if (h264Config?.enableStartBitrate && h264Config.startBitrate) {
          newParams.push(`x-google-start-bitrate=${h264Config.startBitrate}`);
        }

        if (newParams.length > 0) {
          cleanParams += (cleanParams ? ';' : '') + newParams.join(';');
          return `a=fmtp:${h264PayloadType} ${cleanParams}`;
        }
      }
      return line;
    });

    return processedLines.join('\r\n');
  });

  // Reconstruct SDP
  sdp.sdp = processedSections.join('\r\n');

  // Handle feedback types
  if (!preset.videoSend?.disableFeedbackTypes) return sdp;
  const feedbackTypes = preset.videoSend.disableFeedbackTypes;

  if (feedbackTypes.googRemb) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ goog-remb\r\n/g, '');
  }
  if (feedbackTypes.transportCc) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ transport-cc\r\n/g, '');
  }
  if (feedbackTypes.ccmFir) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ ccm fir\r\n/g, '');
  }
  if (feedbackTypes.nack) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ nack\r\n/g, '');
  }
  if (feedbackTypes.nackPli) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ nack pli\r\n/g, '');
  }
  return sdp;
};

export const applyPresetToTransceivers = (
  peerId: string,
  peer: Peer,
  trackId: string,
  preset: Partial<PlaycastWebrtcPreset>,
  peerMetadata: PlaycastPeerClientMetadata,
) => {
  const senders = peer.get().getSenders();
  if (!senders) {
    debug('No sender found');
    // log all sender tracks for debug
    return;
  }
  senders.forEach((sender) => {
    // Clean up this sender if its not doing anything
    if (!sender.track) {
      peer.get().removeTrack(sender);
      return;
    }

    const params: any = sender.getParameters();
    if (!params.encodings) {
      params.encodings = [{}] as RTCRtpEncodingParameters[];
    }

    if (sender.track?.kind === 'audio') {
      // Audio tracks don't have encodings
      return;
    }

    // Apply the preset values to the encoding parameters
    params.encodings = params.encodings.map((encoding: RTCRtpEncodingParameters) => ({
      ...encoding,
      maxBitrate: preset.videoSend?.bitrate ?? encoding.maxBitrate,
      maxFramerate: preset.videoSend?.framerate ?? encoding.maxFramerate,
      scaleResolutionDownBy: 1,
      networkPriority: 'high' as RTCPriorityType,
      priority: 'high' as RTCPriorityType,
    }));
    params.degradationPreference = preset.videoSend?.degradationPreference ?? 'maintain-framerate';

    debug('Applying encoding parameters', params.encodings);
    // only set the params if getParameters() has been called on this sender
    try {
      (sender as RTCRtpSender).setParameters(params);
    } catch (err) {
      debug('Failed to set parameters', err);
    }
  });

  if (preset.videoSend?.keyframeInterval) {
    manageKeyframeInterval.start(peerId, trackId, peer, preset.videoSend.keyframeInterval);
  } else {
    manageKeyframeInterval.stop(peerId, trackId);
  }

  const transcievers = peer.get().getTransceivers();
  transcievers.forEach((transciever) => {
    if (transciever.receiver.track && transciever.receiver.track.kind === 'video') {
      // Set jitterBufferTarget if specified in preset
      if (preset.videoReceive?.jitterBufferTarget !== undefined) {
        try {
          const target = preset.videoReceive.jitterBufferTarget;
          // Ensure target is within valid range (0-4000ms)
          if (target >= 0 && target <= 4000) {
            (transciever.receiver as any).jitterBufferTarget = target;
          } else {
            console.warn(`[Peer] Invalid jitterBufferTarget value: ${target}ms. Must be between 0-4000ms.`);
          }
        } catch (err) {
          console.warn('[Peer] Failed to set jitterBufferTarget:', err);
        }
      }

      // Set codec preferences if specified
      if (transciever.sender.track?.id === trackId && preset.videoReceive?.encoder) {
        const codecPreferences = convertPlaycastEncodingTypeToSdpLines(
          preset.videoReceive.encoder,
          peerMetadata,
          preset.videoReceive?.encoderProfileOverride,
        );
        if (codecPreferences) {
          transciever.setCodecPreferences(codecPreferences);
        }
      } else {
        const codecPreferences = convertPlaycastEncodingTypeToSdpLines(
          defaultQualityPresetStore.currentState.default.videoReceive?.encoder ?? 'h264',
          peerMetadata,
          preset.videoReceive?.encoderProfileOverride,
        );
        if (codecPreferences) {
          transciever.setCodecPreferences(codecPreferences);
        }
      }
    }
  });
};

// export const applyPresetToReceivers = (peer: Peer, presets: PlaycastWebrtcPresetGroup, trackData: {[trackId: string]: PlaycastPeerMediaMetadata}) => {
//   // Iterate tracks, see if they match trackdata. If they do, apply the preset. If they are unknown, apply default.
//   const transcievers = peer.get().getTransceivers();
//   transcievers.forEach((transciever) => {
//     const receiver = transciever.receiver;
//     if(!receiver) return;
//     if(!receiver.track) return;
//     if(receiver.track.kind !== 'video') return;

//     const trackId = receiver.track.id;
//     let metadata = trackData[trackId];
//     if (!metadata) {
//       metadata = { channel: 'default', type: 'video' };
//     }
//     if (metadata.type === 'video') {
//       if (preset.videoReceive?.encoder) {
//         const codecPreferences = convertPlaycastEncodingTypeToSdpLines(preset.videoReceive.encoder);
//         transciever.setCodecPreferences(codecPreferences);
//       }
//     }
//   });
// };

export const applyCodecPreferencesToPeer = (
  peer: Peer,
  presetGroup: PlaycastWebrtcPresetGroup,
  metadata: PlaycastPeerClientMetadata,
  channelId?: PlaycastChannels,
) => {
  const transceivers = peer.get().getTransceivers();

  transceivers.forEach((transceiver) => {
    // Skip if not a video transceiver
    if (!transceiver.receiver.track || transceiver.receiver.track.kind !== 'video') {
      return;
    }

    // If channelId is specified, only apply to that channel's transceiver
    if (channelId) {
      const preset = presetGroup[channelId];
      if (!preset?.videoReceive?.encoder) return;

      // Apply codec preferences
      const codecPreferences = convertPlaycastEncodingTypeToSdpLines(
        preset.videoReceive.encoder,
        metadata,
        preset.videoReceive.encoderProfileOverride,
      );
      if (codecPreferences) {
        transceiver.setCodecPreferences(codecPreferences);
      }

      // Apply jitter buffer target if specified
      if (preset.videoReceive.jitterBufferTarget !== undefined) {
        try {
          const target = preset.videoReceive.jitterBufferTarget;
          if (target >= 0 && target <= 4000) {
            (transceiver.receiver as any).jitterBufferTarget = target;
          } else {
            console.warn(`[Peer] Invalid jitterBufferTarget value: ${target}ms. Must be between 0-4000ms.`);
          }
        } catch (err) {
          console.warn('[Peer] Failed to set jitterBufferTarget:', err);
        }
      }
      return;
    }

    // If no channelId specified, try to match by track type
    Object.entries(presetGroup).forEach(([, preset]) => {
      if (preset.videoReceive?.encoder) {
        // Apply codec preferences
        const codecPreferences = convertPlaycastEncodingTypeToSdpLines(
          preset.videoReceive.encoder,
          metadata,
          preset.videoReceive?.encoderProfileOverride,
        );
        if (codecPreferences) {
          transceiver.setCodecPreferences(codecPreferences);
        }

        // Apply jitter buffer target if specified
        if (preset.videoReceive.jitterBufferTarget !== undefined) {
          try {
            const target = preset.videoReceive.jitterBufferTarget;
            if (target >= 0 && target <= 4000) {
              (transceiver.receiver as any).jitterBufferTarget = target;
            } else {
              console.warn(`[Peer] Invalid jitterBufferTarget value: ${target}ms. Must be between 0-4000ms.`);
            }
          } catch (err) {
            console.warn('[Peer] Failed to set jitterBufferTarget:', err);
          }
        }
      }
    });
  });
};

const keyframeIntervals: Record<string, NodeJS.Timeout> = {};
const getIntervalKey = (peerId: string, trackId: string) => `${peerId}:${trackId}`;

// Manage keyframe intervals
const ARE_KEYFRAMES_DISABLED = true;
export const manageKeyframeInterval = {
  start: (peerId: string, trackId: string, peer: Peer, intervalMs: number) => {
    if (ARE_KEYFRAMES_DISABLED) return;
    const key = getIntervalKey(peerId, trackId);

    // Clear existing interval if it exists
    manageKeyframeInterval.stop(peerId, trackId);

    // Create new interval
    const intervalId = setInterval(() => {
      const senders = peer.get().getSenders();
      senders.forEach((sender) => {
        if (sender?.track?.id === trackId && sender.track.kind === 'video') {
          // also ensure the peer is still connected
          if (peer.get().connectionState !== 'connected') {
            debug('Peer is not connected, stopping keyframe interval');
            manageKeyframeInterval.stop(peerId, trackId);
            return;
          }
          const param = sender.getParameters();
          try {
            debug('Requesting keyframe for', trackId, 'with interval', intervalMs);
            (sender as any).setParameters(param, {
              encodingOptions: [{ keyFrame: true }],
            });
          } catch (err) {
            debug('Failed to request keyframe', err);
          }
        }
      });
    }, intervalMs);

    // Store the interval
    keyframeIntervals[key] = intervalId;

    return intervalId;
  },

  stop: (peerId: string, trackId: string) => {
    const key = getIntervalKey(peerId, trackId);
    const existingInterval = keyframeIntervals[key];
    if (existingInterval) {
      clearInterval(existingInterval);
      delete keyframeIntervals[key];
    }
  },

  stopAllForPeer: (peerId: string) => {
    debug('Stopping all keyframe intervals for peer', peerId);
    // Find and clear all intervals for this peer
    const peerPrefix = `${peerId}:`;
    for (const key in keyframeIntervals) {
      if (key.startsWith(peerPrefix)) {
        clearInterval(keyframeIntervals[key]);
        delete keyframeIntervals[key];
      }
    }
  },

  // Utility function to check if an interval exists
  exists: (peerId: string, trackId: string) => {
    return keyframeIntervals[getIntervalKey(peerId, trackId)] !== undefined;
  },
};

// Helper to get encoder profile for a track
export const getEncoderProfile = (
  track: MediaStreamTrack,
  channelId: PlaycastChannels,
  qualityPresetStore: StateStore<PlaycastWebrtcPresetGroup>,
): PlaycastWebrtcEncoder | PlaycastWebrtcEncoder[] | undefined => {
  const preset = qualityPresetStore.currentState[channelId] ?? qualityPresetStore.currentState.default;
  return preset?.videoReceive?.encoder ?? 'vp8';
};

// Helper to check if encoder profiles match
export const doEncodersMatch = (
  track1: MediaStreamTrack,
  channel1: PlaycastChannels,
  track2: MediaStreamTrack,
  channel2: PlaycastChannels,
  qualityPresetStore: StateStore<PlaycastWebrtcPresetGroup>,
): boolean => {
  const profile1 = getEncoderProfile(track1, channel1, qualityPresetStore);
  const profile2 = getEncoderProfile(track2, channel2, qualityPresetStore);

  // Profiles can be arrays. If any match, return true
  if (Array.isArray(profile1) && Array.isArray(profile2)) {
    return profile1.some((p1) => profile2.includes(p1));
  }
  // Else, compare directly
  return profile1 === profile2;
};
